import { ECommItemType } from '../enums/e-comm-item-type.ts'
import { EnumValue, tryEnumFromValue } from '@core/utils/enums.ts'
import { ensureError } from '@core/utils/ensure-error.ts'

/** @public */
export type ECommItemWithoutQuantity = {
    item_type?: EnumValue<ECommItemType>
    id: string
}

/** @public */
export type ECommItemWithQuantity = ECommItemWithoutQuantity & { quantity: number }

/** @public */
export type ECommItem = ECommItemWithQuantity | ECommItemWithoutQuantity

/** @public */
export type ECommItemWithRating = ECommItemWithoutQuantity & { rating?: number }

/** @internal */
export type ECommActivity = Record<string, (ECommItem | ECommItemWithRating)[] | undefined>

/** @internal */
export type ECommPurchaseActivity = ECommActivity & {
    // on ECommType.PRODUCT only
    purchase_id?: string
    price_value?: string
}

/** @internal */
export function eCommItemOf(data: object | ECommItem, defaultItemType: EnumValue<ECommItemType>): ECommItem {
    let itemType: EnumValue<ECommItemType> | undefined
    let idValue: unknown
    let quantityValue: unknown

    try {
        itemType =
            'item_type' in data
                ? tryEnumFromValue(ECommItemType, data.item_type)
                : 'event_id' in data
                ? ECommItemType.EVENT
                : 'product_id' in data
                ? ECommItemType.PRODUCT
                : defaultItemType

        if ('id' in data) {
            idValue = data.id
        } else if ('event_id' in data) {
            idValue = String(data.event_id)
        } else if ('product_id' in data) {
            idValue = String(data.product_id)
        } else {
            idValue = ''
        }

        quantityValue = 'quantity' in data ? data.quantity : undefined
    } catch (err) {
        throw new TypeError(`ECommItem could not be parsed: ${ensureError(err).message}. ${JSON.stringify(data)}`)
    }

    if (!itemType) {
        throw new TypeError(`ECommItem.item_type could not be parsed. ${JSON.stringify(data)}`)
    }

    if (typeof idValue !== 'string' || !idValue) {
        throw new TypeError(`ECommItem.id could not be parsed. ${JSON.stringify(data)}`)
    }

    if (quantityValue !== null && quantityValue !== undefined && Number.isNaN(Number(quantityValue))) {
        throw new TypeError(`ECommItem.quantity is not numeric. ${JSON.stringify(data)}`)
    }

    const id = idValue as string

    const item = {
        item_type: itemType,
        id: id,
        quantity: quantityValue as number | undefined,
    }

    return quantityValue !== null && quantityValue !== undefined
        ? { item_type: item.item_type, id: item.id, quantity: item.quantity as number }
        : { item_type: item.item_type, id: item.id }
}

function toNumericRating(ratingValue: unknown): number | undefined {
    if (typeof ratingValue === 'number') {
        return ratingValue
    }

    if (typeof ratingValue === 'string' && ratingValue.trim().length) {
        const parsed = Number(ratingValue)
        return Number.isNaN(parsed) ? undefined : parsed
    }

    return undefined
}

function isValidRating(rating: number): boolean {
    if (!Number.isFinite(rating)) {
        return false
    }

    if (rating < 0 || rating > 100) {
        return false
    }

    const scaled = rating * 10
    return Math.abs(scaled - Math.round(scaled)) < 0.000001
}

/** @internal */
export function eCommRatingOf(ratingValue: unknown): number | undefined {
    if (ratingValue === null || ratingValue === undefined) {
        return undefined
    }

    const parsedRating = toNumericRating(ratingValue)
    if (parsedRating === undefined || !isValidRating(parsedRating)) {
        throw new TypeError(`ECommItem.rating is invalid. ${JSON.stringify(ratingValue)}`)
    }

    return parsedRating
}

/** @internal */
export function eCommItemWithRatingOf(
    data: object | ECommItemWithRating,
    defaultItemType: EnumValue<ECommItemType>,
): ECommItemWithRating {
    const item = eCommItemOf(data, defaultItemType)
    const ratingValue = 'rating' in data ? data.rating : undefined
    const parsedRating = eCommRatingOf(ratingValue)

    return parsedRating === undefined
        ? { item_type: item.item_type, id: item.id }
        : {
              item_type: item.item_type,
              id: item.id,
              rating: parsedRating,
          }
}
